import { Controller } from "@hotwired/stimulus"

// Replaces Bootstrap modal functionality with pure Stimulus
export default class extends Controller {
  static targets = ["overlay", "container"]
  static values = {
    preventBackdropClose: { type: Boolean, default: false }
  }

  connect() {
    // Set up ESC key listener for modal closing
    this.keyDownHandler = this.handleKeyDown.bind(this)
    document.addEventListener("keydown", this.keyDownHandler)

    // Make sure body overflow is managed
    this.originalOverflow = document.body.style.overflow

    // Make sure modals are hidden on page load
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("tw-hidden")
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.keyDownHandler)

    // Restore body overflow when modal is destroyed
    document.body.style.overflow = this.originalOverflow
  }

  open(event) {
    if (event) event.preventDefault()

    // Display the modal and backdrop
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("tw-hidden")

      // Prevent scrolling of the body
      document.body.style.overflow = "hidden"

      // Focus the first focusable element
      this.focusFirstElement()
    }
  }

  close(event) {
    if (event) event.preventDefault()

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("tw-hidden")

      // Restore scrolling
      document.body.style.overflow = this.originalOverflow
    }
  }

  // Handle clicks on the backdrop (area outside modal content)
  handleContainerClick(event) {
    // Prevent clicks within the container from closing the modal
    event.stopPropagation()
  }

  handleKeyDown(event) {
    // Only handle keyboard events if the modal is visible
    if (this.isOpen()) {
      if (event.key === "Escape") {
        this.close(event)
      } else if (event.key === "Tab") {
        // Trap focus inside the modal
        this.trapFocus(event)
      }
    }
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // If shift+tab on first element, move to last element
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    }
    // If tab on last element, move to first element
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    } else if (this.hasContainerTarget) {
      // If no focusable elements, focus the container itself
      this.containerTarget.focus()
    }
  }

  getFocusableElements() {
    if (!this.hasContainerTarget) return []

    // Get all focusable elements within the modal
    return Array.from(this.containerTarget.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ))
  }

  isOpen() {
    return this.hasOverlayTarget && !this.overlayTarget.classList.contains("tw-hidden")
  }
}